import { useTranslation } from 'react-i18next';
import {
  Avatar,
  Button,
  Checkbox,
  ColorPicker,
  Divider,
  Form,
  Input,
  Tag,
} from 'antd';
import TagInterface from '../../interfaces/Tag';
import { useEffect, useRef } from 'react';
import tagColors from '../../lib/tagColors';
import clsx from 'clsx';
import { getContrastBackgroundColorForText } from '../../utils/colorsUtilis';

interface Props {
  tag: TagInterface | null;
  onSubmit: (newTag: TagInterface) => void;
  onCancel: () => void;
}

export const TagForm = ({ tag, onSubmit, onCancel }: Props) => {
  const { t } = useTranslation();
  const [form] = Form.useForm();
  Form.useWatch([], form);

  const containerRef = useRef<HTMLDivElement>(null);

  /**
   * Handles the submission of form data, processes and transforms it into a `User` object,
   * and passes the constructed user object to the given submission handler.
   *
   * @param {any} values - The form values submitted by the user. Includes fields such as
   * firstName, lastName, email, role, expirationDate, and privateUser.
   */
  const handleFinish = (values: any) => {
    let newTag: TagInterface = {
      id: tag?.id ?? 0,
      fileVisible: values.fileVisible,
      firstName: values.firstName,
      lastName: values.lastName,
      onBehalfOf: values.onBehalfOf,
      publicTag: values.publicTag,
      tagVisible: values.tagVisible,
      title: values.title,
      userId: values.userId,
      color: values.color,
    };

    if (tag) {
      newTag = { ...tag, ...newTag };
    }
    onSubmit(newTag);
  };

  // Handle color selection manually
  const handleColorSelect = (color: string) => {
    // Using form.setFields instead of setFieldsValue to trigger validation and UI updates
    form.setFields([
      {
        name: 'color',
        value: color,
        touched: true,
      },
    ]);
  };

  useEffect(() => {
    if (tag) {
      const formValues = {
        firstName: tag.firstName || '',
        lastName: tag.lastName || '',
        onBehalfOf: tag.onBehalfOf || '',
        publicTag: tag.publicTag,
        tagVisible: tag.tagVisible,
        title: tag.title,
        userId: tag.userId,
        color: tag.color,
        fileVisible: tag.fileVisible,
      };
      form.setFieldsValue(formValues);
    } else {
      form.resetFields();
    }
  }, [tag, form]);

  return (
    <div ref={containerRef} style={{ position: 'relative' }}>
      <Form
        layout="vertical"
        form={form}
        size="large"
        className="pt-6 [&_.ant-form-item-explain-error]:hidden [&_.ant-form-item-label]:hidden [&_.ant-form-item]:mb-2"
        onFinish={handleFinish}
      >
        <Form.Item name="title" label="Title" rules={[{ required: true }]}>
          <Input placeholder="Title" autoFocus maxLength={35} />
        </Form.Item>

        <Form.Item name="color" label="Color" rules={[{ required: true }]}>
          <div className="flex flex-row gap-2">
            {tagColors.map((color) => (
              <Button
                key={color.text}
                type="text"
                size="small"
                onClick={() => handleColorSelect(color.text)}
              >
                <Avatar
                  className={clsx(
                    'border-gray bg-white',
                    form.getFieldValue('color') === color && 'border-2'
                  )}
                  style={{
                    borderColor:
                      form.getFieldValue('color') === color.text
                        ? color.text
                        : 'lightgray',
                    color: color.text,
                  }}
                  size="small"
                  shape="square"
                >
                  A
                </Avatar>
              </Button>
            ))}
            <ColorPicker
              size="small"
              className="w-full py-1"
              value={form.getFieldValue('color')}
              showText={() => (
                <span>{form.getFieldValue('color') || 'Select Color'}</span>
              )}
              onChangeComplete={(e) => {
                const hexColor = e.toHexString();
                handleColorSelect(hexColor);
              }}
            />
          </div>
        </Form.Item>

        <Divider className="my-3" />
        <div className="flex flex-col gap-2 pb-6">
          <span>Preview</span>
          <Tag
            aria-label={form.getFieldValue('title')}
            style={{
              backgroundColor: form.getFieldValue('color')
                ? getContrastBackgroundColorForText(form.getFieldValue('color'))
                : 'transparent',
            }}
            className="h-10 w-full"
          >
            <div className="flex h-full flex-row items-center justify-center gap-2">
              <span
                className="text-lg"
                style={{
                  color: form.getFieldValue('color'),
                }}
              >
                {form.getFieldValue('title') || t('common.tagTitle')}
              </span>
            </div>
          </Tag>
        </div>
        {/* <pre>{JSON.stringify(form.getFieldsValue(), null, 2)}</pre> */}
        <Form.Item name="publicTag" valuePropName="checked">
          <Checkbox>Company Tag</Checkbox>
        </Form.Item>

        <div className="flex flex-row items-center justify-end gap-2">
          <Button size="middle" onClick={() => onCancel()}>
            {t('common.cancel')}
          </Button>
          <Button size="middle" type="primary" htmlType="submit">
            {t('common.save')}
          </Button>
        </div>
      </Form>
    </div>
  );
};

export default TagForm;
